'use client';

import { Menu, MenuItem, MenuButton, MenuItems } from '@headlessui/react';
import { useCallback, useMemo } from 'react';
import { UseSortByProps } from 'react-instantsearch';
import { ChevronDown, ArrowUpDown, Check } from 'lucide-react';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import DefaultButton from '@/components/common/default/DefaultButton';
import useSortOptions from '@/components/skus/hooks/useSortOptions';

const CustomSortBy = (props: UseSortByProps) => {
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const { initialIndex, currentRefinement, options, refine } = useSortOptions(props);

  const getCurrentRefinementName = useCallback(() => {
    let index = options.find(option => option.value === currentRefinement);
    if (!index) {
      index = options.find(option => option.value === initialIndex);
    }
    return index?.label || translate(CommonEnum.sort_by);
  }, [currentRefinement, initialIndex, options, translate]);

  const currentOption = useMemo(() => {
    return (
      options.find(option => option.value === currentRefinement) ||
      options.find(option => option.value === initialIndex)
    );
  }, [currentRefinement, initialIndex, options]);

  return (
    <>
      <Menu as="div" className="tw-relative tw-inline-block tw-text-left">
        <MenuButton
          as={DefaultButton}
          className="tw-group tw-flex tw-items-center tw-justify-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-platinum-gray tw-bg-white tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-textBlack tw-shadow-sm tw-transition-all tw-duration-200 hover:tw-border-slate-gray hover:tw-bg-gray-50 hover:tw-shadow-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-main focus:tw-ring-offset-2 active:tw-scale-95"
          aria-label={`${translate(CommonEnum.sort_by)}: ${getCurrentRefinementName()}`}
        >
          <div className="tw-align-center tw-flex tw-justify-center tw-rounded-lg tw-bg-platinum-gray/50 tw-p-1.5 tw-transition-colors group-hover:tw-bg-slate-gray/20">
            <ArrowUpDown className="tw-h-4 tw-w-4 tw-text-slate-gray" />
          </div>
          <span className="tw-max-w-32 tw-truncate tw-text-textBlack sm:tw-max-w-none">
            {getCurrentRefinementName()}
          </span>
          <ChevronDown className="tw-h-4 tw-w-4 tw-text-slate-gray tw-transition-transform tw-duration-200 group-data-[open]:tw-rotate-180" />
        </MenuButton>

        <MenuItems
          anchor="bottom end"
          className="tw-z-50 tw-mt-2 tw-w-64 tw-origin-top-right tw-rounded-xl tw-border tw-border-solid tw-border-white/60 tw-bg-white/95 tw-p-2 tw-shadow-md tw-ring-1 tw-ring-black/5 tw-backdrop-blur-sm focus:tw-outline-none"
          transition
        >
          <div className="tw-space-y-1">
            {/* Header */}
            <div className="tw-mb-2 tw-border-b tw-border-platinum-gray/50 tw-pb-2">
              <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
                <ArrowUpDown className="tw-h-4 tw-w-4 tw-text-slate-gray" />
                <span className="tw-text-sm tw-font-semibold tw-text-textBlack">
                  {translate(CommonEnum.sort_by) || 'Sort by'}
                </span>
              </div>
            </div>

            {/* Sort Options */}
            {options.map((sort, index) => (
              <MenuItem key={index}>
                {({ focus }) => (
                  <DefaultButton
                    onClick={() => refine(sort.value)}
                    className={`tw-group tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-transition-all tw-duration-200 ${
                      focus
                        ? 'tw-bg-platinum-gray/30 tw-text-textBlack'
                        : 'tw-text-slate-gray hover:tw-bg-platinum-gray/20 hover:tw-text-textBlack'
                    } ${
                      sort.value === currentRefinement
                        ? 'tw-bg-gradient-to-r tw-from-order-50 tw-to-order-100 tw-text-order-900 tw-ring-1 tw-ring-order-200'
                        : ''
                    }`}
                    aria-pressed={sort.value === currentRefinement}
                  >
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <div
                        className={`tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border-2 tw-transition-all tw-duration-200 ${
                          sort.value === currentRefinement
                            ? 'tw-border-order-500 tw-bg-gradient-to-r tw-from-order tw-to-custom-gold tw-text-white'
                            : 'tw-border-platinum-gray tw-bg-white group-hover:tw-border-slate-gray'
                        }`}
                      >
                        {sort.value === currentRefinement && <Check className="tw-h-3 tw-w-3" />}
                      </div>
                      <span
                        className={`tw-font-medium ${sort.value === currentRefinement ? 'tw-text-order-900' : ''}`}
                      >
                        {sort.label}
                      </span>
                    </div>
                    {sort.value === currentRefinement && (
                      <div className="tw-rounded-full tw-bg-order-100 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-order-800">
                        {translate(CommonEnum.active) || 'Active'}
                      </div>
                    )}
                  </DefaultButton>
                )}
              </MenuItem>
            ))}
          </div>

          {/* Footer with current selection info */}
          {currentOption && (
            <div className="tw-mt-2 tw-border-t tw-border-platinum-gray/50 tw-pt-2">
              <div className="tw-px-3 tw-py-2 tw-text-xs tw-text-slate-gray">
                <span className="tw-font-medium">
                  {translate(CommonEnum.current_sort) || 'Current'}:
                </span>{' '}
                <span className="tw-text-textBlack">{currentOption.label}</span>
              </div>
            </div>
          )}
        </MenuItems>
      </Menu>
    </>
  );
};

export default CustomSortBy;
